import React, { useState, useEffect, useCallback } from 'react';
import Leaderboard from './components/Leaderboard';
import SeasonSelector from './components/SeasonSelector';
import HistoricalView from './components/HistoricalView';
import AllTimeStats from './components/AllTimeStats';
import { StandingsService } from './services/StandingsService';
import InProgressGames from './components/InProgressGames';
import DraftView from './components/DraftView';
import { DraftService } from './services/DraftService';
import { 
  StandingsResponse, 
  HistoricalResponse, 
  AllTimeStatsResponse,
  DraftResponse,
} from '../../shared/types';
import './App.css';

type ViewMode = 'current' | 'draft' | 'historical' | 'all-time';

function App() {
  const [standings, setStandings] = useState<StandingsResponse | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalResponse | null>(null);
  const [allTimeStats, setAllTimeStats] = useState<AllTimeStatsResponse | null>(null);
  const [draftData, setDraftData] = useState<DraftResponse | null>(null);
  const [draftReady, setDraftReady] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [selectedSeason, setSelectedSeason] = useState<number>(new Date().getFullYear());

  const fetchStandings = async (season?: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await StandingsService.fetchStandings(season);
      setStandings(data);
      if (data.season) {
        setSelectedSeason(data.season);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch standings');
      console.error('Error fetching standings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricalData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await StandingsService.fetchHistoricalStandings();
      setHistoricalData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch historical data');
      console.error('Error fetching historical data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTimeStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await StandingsService.fetchAllTimeStats();
      setAllTimeStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch all-time stats');
      console.error('Error fetching all-time stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDraft = useCallback(async (selectWhenIncomplete = false) => {
    try {
      setDraftError(null);
      const data = await DraftService.fetchCurrentDraft();
      setDraftData(data);
      if (!data.available && (data.reason === 'CLOSED' || data.reason === 'MISSING_DEADLINE')) {
        setViewMode(mode => mode === 'draft' ? 'current' : mode);
      }
      if (selectWhenIncomplete && data.available && !data.complete) {
        setViewMode('draft');
      }
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : 'Failed to fetch draft');
      console.error('Error fetching draft:', err);
    } finally {
      setDraftReady(true);
    }
  }, []);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    
    switch (mode) {
      case 'draft':
        fetchDraft();
        break;
      case 'current':
        if (!standings) {
          fetchStandings();
        }
        break;
      case 'historical':
        if (!historicalData) {
          fetchHistoricalData();
        }
        break;
      case 'all-time':
        if (!allTimeStats) {
          fetchAllTimeStats();
        }
        break;
    }
  };

  const handleSeasonChange = (season: number) => {
    setSelectedSeason(season);
    if (viewMode === 'current') {
      fetchStandings(season);
    }
  };

  const handleSeasonSelect = (season: number) => {
    setViewMode('current');
    setSelectedSeason(season);
    fetchStandings(season);
  };

  useEffect(() => {
    fetchStandings();
  }, []);

  useEffect(() => {
    fetchDraft(true);
  }, [fetchDraft]);

  useEffect(() => {
    if (viewMode !== 'current') return;
    const interval = setInterval(() => {
      fetchStandings(selectedSeason);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [viewMode, selectedSeason]);

  useEffect(() => {
    if (viewMode !== 'draft') return;
    const interval = setInterval(() => fetchDraft(), 5 * 1000);
    return () => clearInterval(interval);
  }, [viewMode, fetchDraft]);

  const isLoading = !draftReady || (loading && (
    (viewMode === 'current' && !standings) ||
    (viewMode === 'historical' && !historicalData) ||
    (viewMode === 'all-time' && !allTimeStats)
  ));

  const hasError = error && (
    (viewMode === 'current' && !standings) ||
    (viewMode === 'historical' && !historicalData) ||
    (viewMode === 'all-time' && !allTimeStats)
  );

  const availableSeasons = historicalData?.availableYears || [new Date().getFullYear()];
  const showDraftTab = !!draftData && (
    draftData.available ||
    draftData.reason === 'MISSING_PRIOR_STANDINGS' ||
    draftData.reason === 'INVALID_PRIOR_STANDINGS'
  );

  if (isLoading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {viewMode === 'draft' ? 'draft' : viewMode === 'current' ? 'standings' : viewMode === 'historical' ? 'historical data' : 'all-time statistics'}...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="App">
        <div className="error-container">
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button 
            onClick={() => {
              if (viewMode === 'current') fetchStandings(selectedSeason);
              else if (viewMode === 'historical') fetchHistoricalData();
              else fetchAllTimeStats();
            }} 
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="app-header">
        <h1>Franchise Fantasy League</h1>
        
        <div className="view-selector">
          {showDraftTab && (
            <button
              className={`view-button ${viewMode === 'draft' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('draft')}
            >
              Draft
            </button>
          )}
          <button 
            className={`view-button ${viewMode === 'current' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('current')}
          >
            Current Season
          </button>
          <button 
            className={`view-button ${viewMode === 'historical' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('historical')}
          >
            Historical
          </button>
          <button 
            className={`view-button ${viewMode === 'all-time' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('all-time')}
          >
            All-Time Stats
          </button>
        </div>
      </div>

      {viewMode === 'draft' && draftData?.available && (
        <>
          {draftError && <div className="draft-page-error" role="alert">{draftError}</div>}
          <DraftView
            draft={draftData}
            onDraftChange={(updated) => {
              setDraftData(updated);
              setDraftError(null);
              if (updated.complete) fetchStandings(updated.season);
            }}
            onRefresh={() => fetchDraft()}
          />
        </>
      )}

      {viewMode === 'draft' && draftData && !draftData.available && showDraftTab && (
        <section className="draft-unavailable card">
          <h2>Draft unavailable</h2>
          <p>
            {draftData.reason === 'MISSING_PRIOR_STANDINGS'
              ? `Final ${draftData.season - 1} standings must be archived before drafting can begin.`
              : `Final ${draftData.season - 1} standings do not contain one valid rank for every league member.`}
          </p>
          {draftError && <p role="alert">{draftError}</p>}
          <button className="retry-button" onClick={() => fetchDraft()}>Check again</button>
        </section>
      )}

      {viewMode === 'current' && (
        <div className="current-view">
          <SeasonSelector
            selectedSeason={selectedSeason}
            availableSeasons={availableSeasons}
            onSeasonChange={handleSeasonChange}
            isLoading={loading}
          />
          
          {standings && (
            <>
              <Leaderboard 
                standings={standings.standings}
                lastUpdated={standings.lastUpdated}
              />

              {/* Live games below the standings */}
              <InProgressGames
                ownerByTeam={(standings.standings || []).reduce((acc, entry) => {
                  (entry.teams || []).forEach(t => { acc[t] = entry.name; });
                  return acc;
                }, {} as Record<string,string>)}
              />
            </>
          )}
          
          <div className="refresh-container">
            <button 
              onClick={() => fetchStandings(selectedSeason)} 
              className="refresh-button"
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      )}

      {viewMode === 'historical' && historicalData && (
        <HistoricalView 
          historicalData={historicalData.seasons}
          onSeasonSelect={handleSeasonSelect}
        />
      )}

      {viewMode === 'all-time' && allTimeStats && (
        <AllTimeStats allTimeStats={allTimeStats} />
      )}
    </div>
  );
}

export default App;
